import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  rectSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import { Fragment } from 'react';

import { cn } from '@Utils/cn';

import {
  DEFAULT_PREVIEW_REGISTRY,
  getSectionChildLayoutClass,
  tokensToCssVars,
} from '../PreviewBlocks';
import PreviewToolbar from '../PreviewToolbar';
import type {
  AssetSummary,
  BlockNode,
  BlockRegistry,
  PreviewCallbacks,
  PreviewRegistry,
  ThemeTokens,
} from '../types';
import { findBlock, findParentBlock } from '../utils';

interface PreviewProps extends PreviewCallbacks {
  /** The slot's `region` root. */
  root: BlockNode;
  registry: BlockRegistry;
  /** `type@version` → renderer. Defaults to the launch set. */
  previewRegistry?: PreviewRegistry;
  /**
   * Page chrome wrapped around the rendered region (see PreviewSkins) so
   * the preview shows the app screen, not bare blocks. Undefined = none.
   */
  skin?: React.ComponentType<{ children: React.ReactNode }>;
  tokens: ThemeTokens;
  selectedId?: string;
  resolveAssetRef?: (ref: string) => AssetSummary | undefined;
  /**
   * "Add section" mode: selection chrome is suppressed, blocks are inert,
   * and insertion points render between top-level blocks. Clicking one fires
   * onInsertAt with the index the new section should take among the root's
   * children.
   */
  insertMode?: boolean;
  onInsertAt?: (index: number) => void;
}

/** Purple line + plus marking one insertion slot between sections. */
function InsertPoint({
  index,
  onInsertAt,
}: {
  index: number;
  onInsertAt?: (index: number) => void;
}) {
  return (
    <button
      type="button"
      aria-label={`Insert at position ${index + 1}`}
      onClick={() => onInsertAt?.(index)}
      className="group relative z-10 -my-3 flex w-full items-center justify-center py-2"
    >
      <span className="absolute inset-x-1 h-1 rounded bg-violet-500 group-hover:bg-violet-400" />
      <span className="relative flex size-6 items-center justify-center rounded-full bg-violet-500 text-white group-hover:bg-violet-400">
        <Plus size={14} />
      </span>
    </button>
  );
}

interface PreviewNodeProps extends PreviewCallbacks {
  node: BlockNode;
  parentId: string;
  index: number;
  siblingCount: number;
  registry: BlockRegistry;
  previewRegistry: PreviewRegistry;
  selectedId?: string;
  resolveAssetRef?: (ref: string) => AssetSummary | undefined;
  /**
   * Parent-imposed layout for this node's wrapper (the preview's flex item),
   * e.g. half-width inside a card-grid section. Mirrors the class the app
   * renderer puts on its own child wrapper.
   */
  layoutClassName?: string;
}

function PreviewNode({
  node,
  parentId,
  index,
  siblingCount,
  registry,
  previewRegistry,
  selectedId,
  resolveAssetRef,
  layoutClassName,
  ...callbacks
}: PreviewNodeProps) {
  // The whole block (chrome included) is the sortable unit; the grip in the
  // selection toolbar is the handle. Hooks run before the hidden early
  // return to keep hook order stable across renders.
  const reorderable = node.locked?.reorder !== false;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.id, disabled: !reorderable });

  // The eye toggle: hidden blocks are stripped from the render, same as the
  // read-path resolve will do.
  if (node.hidden) return null;

  const def = registry[node.type];
  const selected = node.id === selectedId;
  // Outline the parent chain of the selection with a thinner line.
  const containsSelection =
    !selected &&
    selectedId !== undefined &&
    Boolean(findBlock(node, selectedId));

  const Component = previewRegistry[node.type];

  const children = (
    <SortableContext
      items={node.children.map((c) => c.id)}
      // 2-column sections lay children out in a grid — sort accordingly.
      strategy={
        node.props.cardGrid === '2-column'
          ? rectSortingStrategy
          : verticalListSortingStrategy
      }
    >
      {node.children.map((child, i) => (
        <PreviewNode
          key={child.id}
          node={child}
          parentId={node.id}
          index={i}
          siblingCount={node.children.length}
          registry={registry}
          previewRegistry={previewRegistry}
          selectedId={selectedId}
          resolveAssetRef={resolveAssetRef}
          layoutClassName={getSectionChildLayoutClass(node, child)}
          {...callbacks}
        />
      ))}
    </SortableContext>
  );

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: transform
          ? `translate(${transform.x}px, ${transform.y}px)`
          : undefined,
        transition: isDragging ? 'none' : transition,
      }}
      className={cn(
        'relative',
        layoutClassName,
        selected && 'outline-2 outline-violet-500',
        containsSelection && 'outline-1 outline-violet-400',
        isDragging && 'z-30 opacity-80',
      )}
      onClick={(e) => {
        // Deepest block wins; without this a card click would bubble up and
        // select its section.
        e.stopPropagation();
        callbacks.onSelect(node.id);
      }}
    >
      {selected && (
        <PreviewToolbar
          label={node.name || def?.displayName || node.type}
          canMoveUp={node.locked?.reorder !== false && index > 0}
          canMoveDown={
            node.locked?.reorder !== false && index < siblingCount - 1
          }
          canDelete={node.locked?.delete !== false}
          canDuplicate={def?.placeable !== false}
          dragHandleProps={
            reorderable ? { ...attributes, ...listeners } : undefined
          }
          onMoveUp={() => callbacks.onReorder(parentId, index, index - 1)}
          onMoveDown={() => callbacks.onReorder(parentId, index, index + 1)}
          onDuplicate={() => callbacks.onDuplicate(node.id)}
          onDelete={() => callbacks.onDelete(node.id)}
        />
      )}
      {Component ? (
        // interactive={false}: actions (drawers, links, delegates) render
        // inert in the editor — clicks select blocks, never fire actions.
        <Component
          node={node}
          resolveAssetRef={resolveAssetRef}
          interactive={false}
        >
          {children}
        </Component>
      ) : (
        // Unknown block type: visible error placeholder in the editor
        // preview (spec §10) — production resolve drops it silently.
        <div className="m-2 rounded border border-dashed border-red-400 p-3 text-xs text-red-400">
          Unknown block: {node.type}
        </div>
      )}
    </div>
  );
}

/** Phone-frame preview rendering the draft tree with the real block set. */
export default function Preview({
  root,
  registry,
  previewRegistry = DEFAULT_PREVIEW_REGISTRY,
  tokens,
  selectedId,
  resolveAssetRef,
  insertMode = false,
  onInsertAt,
  skin: Skin,
  ...callbacks
}: PreviewProps) {
  const sensors = useSensors(
    // A small activation distance keeps plain clicks (select, toolbar
    // actions) from starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  // Same rules as the layers panel: sorting is same-parent only, and
  // collisions are computed against the dragged block's siblings so
  // container rects can't hijack the geometry (see LayerTree).
  const collisionDetection: CollisionDetection = (args) => {
    const parent = findParentBlock(root, String(args.active.id));
    if (!parent) return closestCenter(args);
    const siblingIds = new Set(parent.children.map((c) => c.id));
    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((container) =>
        siblingIds.has(String(container.id)),
      ),
    });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const activeParent = findParentBlock(root, String(active.id));
    const overParent = findParentBlock(root, String(over.id));
    if (!activeParent || activeParent.id !== overParent?.id) return;
    const fromIndex = activeParent.children.findIndex(
      (c) => c.id === active.id,
    );
    const toIndex = activeParent.children.findIndex((c) => c.id === over.id);
    if (fromIndex === -1 || toIndex === -1) return;
    callbacks.onReorder(activeParent.id, fromIndex, toIndex);
  };

  const region = (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={root.children.map((c) => c.id)}
        strategy={verticalListSortingStrategy}
      >
        {root.children.map((node, i) => (
          <Fragment key={node.id}>
            {insertMode && <InsertPoint index={i} onInsertAt={onInsertAt} />}
            {/* In insert mode the content is inert — only the insertion
                points are interactive — and selection chrome is hidden. */}
            <div className={cn(insertMode && 'pointer-events-none')}>
              <PreviewNode
                node={node}
                parentId={root.id}
                index={i}
                siblingCount={root.children.length}
                registry={registry}
                previewRegistry={previewRegistry}
                selectedId={insertMode ? undefined : selectedId}
                resolveAssetRef={resolveAssetRef}
                {...callbacks}
              />
            </div>
          </Fragment>
        ))}
      </SortableContext>
      {insertMode && (
        <InsertPoint index={root.children.length} onInsertAt={onInsertAt} />
      )}
    </DndContext>
  );

  return (
    <div className="dark h-fit w-108 shrink-0 overflow-hidden rounded-xl bg-black">
      {/* The region root emits the theme tokens as CSS variables for its
          subtree (spec §7.4); every block below consumes variables only. */}
      <div
        className="font-montserrat min-h-150 cursor-default px-0.5 py-2"
        style={{
          ...tokensToCssVars(tokens),
          backgroundColor: 'var(--color-bg)',
          ...(tokens.font?.body
            ? { fontFamily: 'var(--font-body)' }
            : undefined),
        }}
        // Blocks render real anchors for external actions; in the editor a
        // click must select the block, never navigate the admin away.
        onClickCapture={(e) => {
          if ((e.target as HTMLElement).closest('a')) e.preventDefault();
        }}
      >
        {/* The skin draws the app's page chrome around the region; editor
            interactions live on the blocks, so wrapping them is inert. */}
        {Skin ? <Skin>{region}</Skin> : region}
      </div>
    </div>
  );
}
